import React, { FC, useEffect, useState, startTransition } from 'react';
import { useParams } from 'react-router-dom';
import { useSnackbar } from 'src/view/components/SnackbarService';
import { useAppState } from 'src/state-management/state-provider';
import FullPageMessage from 'src/view/components/FullPageMessage';
import FullPageLoader from 'src/view/components/FullPageLoader';
import { Home } from '@mui/icons-material';
import clientService from 'src/clientService';

interface BioDetailsFetcherWrapperProps {
  children: React.ReactNode;
}

const BioDetailsFetcherWrapper: FC<BioDetailsFetcherWrapperProps> = ({ children }) => {
  const { id, siteId } = useParams<{ id: string; siteId: string }>();
  const { dispatch } = useAppState();
  const { showSnackbar, snackbarType } = useSnackbar();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      setIsLoading(true);
      setError(null);

      clientService.api
        .readBiodiversityProjectSummaryApiV1ProjectsProjectIdBiodiversitySummaryGet(id)
        .then((response) => {
          startTransition(() => {
            dispatch({
              type: 'SET_PROJECT_BIODIVERSITY_ANALYSIS',
              payload: response.data,
            });
            reset();
          });
        })
        .catch((apiError) => {
          console.error('Failed to fetch bio diversity analysis details:', apiError);
          const errorMessage =
            apiError.response?.data?.detail ||
            `Failed to load bio diversity analysis details for ID: ${id}.`;
          startTransition(() => {
            setError(errorMessage);
            showSnackbar({
              message: 'Error fetching bio diversity analysis details.',
              type: snackbarType.ERROR,
            });
            reset();
          });
        });
      clientService.api
        .readBiodiversityAllSitesForProjectApiV1ProjectsProjectIdBiodiversitySitesGet(id)
        .then((response) => {
          startTransition(() => {
            dispatch({
              type: 'SET_SITE_BIODIVERSITY_ANALYSIS',
              payload: response.data,
            });
          });
        })
        .catch((apiError) => {
          const errorMessage =
            apiError.response?.data?.detail ||
            `Failed to load bio bio diversity site analysis details for ID: ${id}.`;
          startTransition(() => {
            setError(errorMessage);
            showSnackbar({
              message: 'Error fetching bio diversity site analysis details.',
              type: snackbarType.ERROR,
            });
            reset();
          });
        });

    } else {
      startTransition(() => {
        setError('Project ID is missing in the route parameters.');
        showSnackbar({ message: 'Project ID missing.', type: snackbarType.ERROR });
        reset();
      });
    }
    return () => {
      reset();
    };
  }, [id, siteId]);

  const reset = () => {
    setIsLoading(false);
    setError(null);
  };

  if (isLoading) {
    return (
      <FullPageLoader
        title="Just a moment, crafting your project view..."
        message="Almost there..."
      />
    );
  }

  if (error) {
    return (
      <FullPageMessage
        headline="Uh-oh! The project data took an unexpected detour."
        subHeadline={`We're having a little trouble loading the details. The system reports: ${error}`}
        ctaText="Go to Home"
        onCtaClick={() => {
          window.location.href = '/';
        }}
        ctaIcon={<Home />}
      />
    );
  }

  return <>{children}</>;
};

export default BioDetailsFetcherWrapper;
